import { useEffect, useRef, useState } from 'react';

import { useElementShown } from '@hh.ru/analytics-js';
import regionBannerConfirmButtonClick from '@hh.ru/analytics-js-events/build/career_platform/region/region_banner_confirm_button_click';
import regionBannerConfirmElementShown from '@hh.ru/analytics-js-events/build/career_platform/region/region_banner_confirm_element_shown';
import regionChooseFormSubmit from '@hh.ru/analytics-js-events/build/career_platform/region/region_choose_form_submit';
import regionChooseScreenShown from '@hh.ru/analytics-js-events/build/career_platform/region/region_choose_screen_shown';
import { TranslatedComponent, translation, useSelector, format } from '@hh.ru/front-static-app';
import { Button, useBreakpoint } from '@hh.ru/magritte-ui';
import { Tooltip } from '@hh.ru/magritte-ui-tooltip';
import { MinusCircleFilledSize24 } from '@hh.ru/magritte-ui/icon';

import { INDEX_ROUTE, LK_ROUTE, PROFESSION_ROUTE } from 'src/app/routesUtils';
import MagritteBannerFacade from 'src/components/MagritteBannerFacade';
import useFeatureEnabled from 'src/hooks/useFeatureEnabled';
import useProfile from 'src/hooks/useProfile';
import useSafeState from 'src/hooks/useSafeState';
import useSnackbar from 'src/hooks/useSnackbar';
import useUserArea from 'src/hooks/useUserArea';
import { UserAreaStatus } from 'src/models/userArea';
import { AreaLeavesDto } from 'src/types/area';
import getDataQa from 'src/utils/getDataQa';

import changeGeoLocationCity from 'src/widgets/GeoLocation/api/changeGeoLocationCity';
import GeoLocationButton from 'src/widgets/GeoLocation/components/GeoLocationButton';
import GeoLocationSearchDialog from 'src/widgets/GeoLocation/components/GeoLocationSearchDialog';

import styles from './styles.less';

const Features = {
    isEnabledGeoButton: 'enabled_geo_location_button',
};

const TrlKeys = {
    confirmCity: {
        title: 'career-platform.widgets.geo-location.confirm-city.title',
        description: 'career-platform.widgets.geo-location.confirm-city.description',
        confirmButton: 'career-platform.widgets.geo-location.confirm-city.confirm-button',
        changeButton: 'career-platform.widgets.geo-location.confirm-city.change-button',
    },
    snack: {
        changeCityError: 'career-platform.widgets.geo-location.snack.change-city-error',
        confirmCityError: 'career-platform.widgets.geo-location.snack.confirm-city-error',
    },
};

interface Props {
    type: 'button' | 'mobile-banner';
}

const DATA_QA_CONFIRM_CITY_BLOCK = 'confirm-geo-location';

// Добавляя в этот перечень страницу не забудьте добавить баннер в мобильном виде на страницу
// Т.к. баннер находится в контенте страницы и может располагаться в любом месте, то этот процесс нельзя автоматизировать или оставить в шапке
// На странице лендоса только тултип
const SHOW_TOOLTIP_PATHS = [INDEX_ROUTE, LK_ROUTE, PROFESSION_ROUTE];

const GeoLocation: TranslatedComponent<Props> = ({ type, trls }) => {
    const isEnabledGeoButton = useFeatureEnabled(Features.isEnabledGeoButton);
    const { areaStatus, area: initialArea, onPushQueryArea } = useUserArea();
    const { isAuth } = useProfile();
    const { isMobile } = useBreakpoint();
    const { showSnack, hideSnack } = useSnackbar();
    const routerLocation = useSelector((state) => state.router.location);

    const [visibleDialog, setVisibleDialog] = useState(false);
    const [visibleConfirmCity, setVisibleConfirmCity] = useState(false);
    // храним в стейте, чтобы после смены города он сразу отобразился пользователю и после перезагрузился
    const [selectedCity, setSelectedCity] = useSafeState(initialArea);

    const tooltipActivatorRef = useRef<HTMLButtonElement>(null);
    const bannerRef = useElementShown(regionBannerConfirmElementShown, {
        areaId: selectedCity.id,
        areaName: selectedCity.text,
    });

    const confirmCityText = {
        title: format(trls[TrlKeys.confirmCity.title], { '{city}': selectedCity.text }),
        description: trls[TrlKeys.confirmCity.description],
        confirmButton: trls[TrlKeys.confirmCity.confirmButton],
        changeButton: trls[TrlKeys.confirmCity.changeButton],
    };

    const onOpenDialog = () => {
        setVisibleConfirmCity(false);
        setVisibleDialog(true);
        regionChooseScreenShown();
    };

    const handleOpenDialog = () => onOpenDialog();

    const handleCloseDialog = () => setVisibleDialog(false);

    const handleConfirmDefaultCity = async () => {
        try {
            await changeGeoLocationCity({ areaId: selectedCity.id, isAuth });
            setVisibleConfirmCity(false);
            regionBannerConfirmButtonClick({
                buttonName: 'correct_region',
                areaId: selectedCity.id,
                areaName: selectedCity.text,
            });
        } catch {
            showSnack({
                children: trls[TrlKeys.snack.confirmCityError],
                addon: <MinusCircleFilledSize24 initial="negative" />,
                onClose: hideSnack,
                showClose: true,
            });
        }
    };

    const handleChangeDefaultCity = () => {
        onOpenDialog();
        regionBannerConfirmButtonClick({
            buttonName: 'wrong_region',
            areaId: selectedCity.id,
            areaName: selectedCity.text,
        });
    };

    const handleSelectCity = async (city: AreaLeavesDto) => {
        try {
            setSelectedCity(city);
            await changeGeoLocationCity({ areaId: city.id, isAuth });
            regionChooseFormSubmit({
                areaId: city.id,
                areaName: city.text,
            });
            // изменяем city.id в урл, чтобы обновить данные на странице без перезагрузки выполнив спа переход по новому URL
            onPushQueryArea(city.id);
        } catch {
            showSnack({
                children: trls[TrlKeys.snack.changeCityError],
                addon: <MinusCircleFilledSize24 initial="negative" />,
                onClose: hideSnack,
                showClose: true,
            });
        }
    };

    useEffect(() => {
        // конвертируем pathname profession/117 в profession/:id чтобы можно было сравнить страницы
        const convertedPathname = routerLocation.pathname.replace(/\/profession\/[^/]+/, '/profession/:id');
        const isAllowedPage = SHOW_TOOLTIP_PATHS.some((path) => path === convertedPathname);
        if (isAllowedPage && areaStatus === UserAreaStatus.Unconfirmed) {
            setVisibleConfirmCity(true);
        }
    }, [routerLocation.pathname, areaStatus]);

    if (!isEnabledGeoButton) {
        return null;
    }

    return (
        <>
            {type === 'button' && (
                <>
                    <GeoLocationButton
                        cityName={selectedCity.text}
                        onClick={handleOpenDialog}
                        ref={tooltipActivatorRef}
                    />
                    <Tooltip
                        maxWidth={400}
                        activatorRef={tooltipActivatorRef}
                        visible={visibleConfirmCity}
                        placement="bottom-right"
                        role="tooltip"
                        autoFocusWhenOpened={false}
                        title={confirmCityText.title}
                        buttons={() => (
                            <>
                                <Button
                                    stretched
                                    style="contrast"
                                    mode="primary"
                                    size="small"
                                    onClick={handleConfirmDefaultCity}
                                    data-qa={getDataQa(DATA_QA_CONFIRM_CITY_BLOCK, 'confirm')}
                                >
                                    {confirmCityText.confirmButton}
                                </Button>
                                <Button
                                    stretched
                                    style="contrast"
                                    mode="secondary"
                                    size="small"
                                    onClick={handleChangeDefaultCity}
                                    data-qa={getDataQa(DATA_QA_CONFIRM_CITY_BLOCK, 'change')}
                                >
                                    {confirmCityText.changeButton}
                                </Button>
                            </>
                        )}
                        closeByClickOutside={false}
                    >
                        <span ref={bannerRef}>{confirmCityText.description}</span>
                    </Tooltip>
                </>
            )}
            {type === 'mobile-banner' && isMobile && visibleConfirmCity && (
                <div className={styles.bannerContainer} ref={bannerRef}>
                    <MagritteBannerFacade
                        visible={visibleConfirmCity}
                        title={confirmCityText.title}
                        description={confirmCityText.description}
                        buttonBase={confirmCityText.confirmButton}
                        buttonAdditional={confirmCityText.changeButton}
                        buttonBaseProps={{
                            onClick: handleConfirmDefaultCity,
                            'data-qa': getDataQa(DATA_QA_CONFIRM_CITY_BLOCK, 'confirm'),
                        }}
                        buttonAdditionalProps={{
                            onClick: handleChangeDefaultCity,
                            'data-qa': getDataQa(DATA_QA_CONFIRM_CITY_BLOCK, 'change'),
                        }}
                        showClose={false}
                        stretched
                        dataQa={getDataQa(DATA_QA_CONFIRM_CITY_BLOCK)}
                    />
                </div>
            )}
            <GeoLocationSearchDialog
                visible={visibleDialog}
                selectedCity={selectedCity}
                onSelectCity={handleSelectCity}
                onClose={handleCloseDialog}
            />
        </>
    );
};

export default translation(GeoLocation);
